import React, { useState } from 'react';
import { Button } from '../common/Button';
import { ComplianceMode } from '../../services/disclaimerService';

export interface ConfidentialityDisclaimerModalProps {
  isOpen: boolean;
  text: string;
  version: number | null;
  hospitalName: string | null;
  complianceMode: ComplianceMode;
  onAccept: () => Promise<void> | void;
  onDecline: () => Promise<void> | void;
}

interface ModeConfig {
  title: string;
  subtitle: string;
  noticeBar: string | null;
  headerBg: string;
  headerIcon: string;
  checkboxLabel: string;
  acceptLabel: string;
}

/** Visual + wording presets per compliance mode. */
const MODE_PRESETS: Record<ComplianceMode, ModeConfig> = {
  USER_RESPONSIBILITY: {
    title: 'Personal Responsibility Consent Required',
    subtitle: 'HIPAA safeguards are not fully implemented on this deployment.',
    noticeBar:
      'You are personally responsible for ensuring no patient identifiers or PHI are entered on this platform.',
    headerBg: 'bg-red-50 border-red-200',
    headerIcon: '🛑',
    checkboxLabel:
      'I have read and understood the limitations above. I personally consent and commit that I will NOT enter, upload, paste, or transmit any Protected Health Information or patient identifiers on this platform, and I accept personal responsibility if I do.',
    acceptLabel: 'I Consent and Accept Responsibility',
  },
  FULL_HIPAA: {
    title: 'Confidentiality Acknowledgment',
    subtitle: 'Review and acknowledge your organization\u2019s acceptable-use terms to continue.',
    noticeBar: null,
    headerBg: 'bg-amber-50 border-amber-200',
    headerIcon: '⚠️',
    checkboxLabel:
      'I have read, understood, and agree to the terms above. I will follow HIPAA minimum-necessary practices and my organization\u2019s privacy policies.',
    acceptLabel: 'I Acknowledge and Accept',
  },
  DISABLED: {
    // Shouldn't render in DISABLED mode, but keep a safe fallback.
    title: 'Acknowledgment',
    subtitle: '',
    noticeBar: null,
    headerBg: 'bg-gray-50 border-gray-200',
    headerIcon: 'ℹ️',
    checkboxLabel: 'I acknowledge.',
    acceptLabel: 'Accept',
  },
};

/**
 * Blocking modal shown after login (before dashboard access) and when a
 * hospital's disclaimer version is bumped. Appearance and wording adapt to
 * the hospital's `compliance_mode`.
 */
export const ConfidentialityDisclaimerModal: React.FC<ConfidentialityDisclaimerModalProps> = ({
  isOpen,
  text,
  version,
  hospitalName,
  complianceMode,
  onAccept,
  onDecline,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const preset = MODE_PRESETS[complianceMode] ?? MODE_PRESETS.USER_RESPONSIBILITY;

  const handleAccept = async () => {
    if (!acknowledged || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAccept();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Unable to record acceptance. Please try again.');
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onDecline();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-70 overflow-y-auto h-full w-full z-[1000] flex items-start justify-center py-8 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimer-modal-title"
    >
      <div className="relative mx-auto w-full max-w-2xl bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className={`px-6 py-4 border-b rounded-t-lg flex-shrink-0 ${preset.headerBg}`}>
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0" aria-hidden="true">{preset.headerIcon}</span>
            <div>
              <h2 id="disclaimer-modal-title" className="text-lg font-bold text-gray-900">
                {preset.title}
              </h2>
              {preset.subtitle && (
                <p className="text-sm text-gray-800 mt-1">{preset.subtitle}</p>
              )}
              <p className="text-xs text-gray-700 mt-1">
                {hospitalName ? <>For <strong>{hospitalName}</strong></> : 'For this hospital'}
                {version !== null && <> • policy v{version}</>}
              </p>
            </div>
          </div>
        </div>

        {/* Prominent responsibility notice (USER_RESPONSIBILITY only) */}
        {preset.noticeBar && (
          <div className="px-6 py-2.5 bg-red-100 border-b border-red-200 flex-shrink-0">
            <p className="text-sm font-semibold text-red-900">
              {preset.noticeBar}
            </p>
          </div>
        )}

        {/* Body — scrollable */}
        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
          <div
            className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed font-sans"
            data-testid="disclaimer-text"
          >
            {text}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
          {error && (
            <div className="mb-3 p-2.5 rounded-md bg-red-50 border border-red-200">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <label className="flex items-start gap-3 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={submitting}
              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              data-testid="disclaimer-checkbox"
            />
            <span className="text-sm text-gray-800">{preset.checkboxLabel}</span>
          </label>

          <div className="flex flex-col sm:flex-row-reverse gap-3">
            <Button
              type="button"
              onClick={handleAccept}
              loading={submitting && acknowledged}
              disabled={!acknowledged || submitting}
              data-testid="disclaimer-accept"
            >
              {preset.acceptLabel}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleDecline}
              disabled={submitting}
              data-testid="disclaimer-decline"
            >
              Decline and Log Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfidentialityDisclaimerModal;
